/**
 * Product type names that are themselves downloads (per category filter mapping).
 * For these we don't append " and download" when hasDownloads is true.
 */
const DOWNLOAD_PRODUCT_TYPE_NAMES = ['download', 'ebook'] as const

export function isDownloadProductType(name: string): boolean {
  const normalized = name.trim().toLowerCase()
  if (normalized.includes('download')) return true
  return DOWNLOAD_PRODUCT_TYPE_NAMES.some(
    t => normalized === t || normalized.startsWith(t + ' ')
  )
}

function formatPreorderCalendarDay(date: Date, anchor: Date): string {
  const includeYear = date.getFullYear() !== anchor.getFullYear()
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' as const } : {}),
  })
}

function parsePreorderReleaseDate(
  preorderRelease: string | Date | null | undefined
): Date | null {
  if (!preorderRelease) return null
  const date =
    typeof preorderRelease === 'string'
      ? new Date(preorderRelease)
      : preorderRelease
  if (isNaN(date.getTime())) return null
  return date
}

function calendarDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function isPreorderReleaseTodayOrFuture(
  preorderRelease: string | Date | null | undefined,
  anchor = new Date()
): boolean {
  const date = parsePreorderReleaseDate(preorderRelease)
  if (!date) return false
  return calendarDay(date).getTime() >= calendarDay(anchor).getTime()
}

/**
 * Returns preorder suffix for product type line, e.g. " (preorder: expected today)" or
 * " (pre-order - launches Mar 20)" when a future release date exists.
 */
export function getPreorderSuffix(
  preorderRelease: string | Date | null | undefined,
  anchor = new Date()
): string {
  const date = parsePreorderReleaseDate(preorderRelease)
  if (!date) return ' (pre-order)'
  const today = calendarDay(anchor)
  const releaseDay = calendarDay(date)
  if (releaseDay.getTime() < today.getTime()) return ' (pre-order)'
  if (releaseDay.getTime() === today.getTime()) return ' (preorder: expected today)'
  const formatted = formatPreorderCalendarDay(date, today)
  return ` (pre-order - launches ${formatted})`
}

/**
 * Format product type for display. When the product has a download option and the
 * type is not a download product type, show "{type} and download".
 * Optionally appends preorder suffix when isPreorder is true.
 */
export function formatProductTypeDisplay(
  productTypeName: string | null | undefined,
  hasDownloads?: boolean,
  isPreorder?: boolean,
  preorderRelease?: string | Date | null
): string {
  if (!productTypeName) return ''
  let base = productTypeName
  if (hasDownloads && !isDownloadProductType(productTypeName)) base = `${productTypeName} and download`
  if (isPreorder) base += getPreorderSuffix(preorderRelease ?? null)
  return base
}
